import * as echarts from "echarts";
import { sortByMonthYear, formatTableCellValue } from "./utils";
export const getTrendChartOption = (chartData) => {
  const sortedData = sortByMonthYear(chartData || []);
  const labels = sortedData.map((d) => d.Month_Year.replace("_", " "));

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      confine: true,
      valueFormatter: (value) => `${formatTableCellValue(value)} Cr`,
    },
    legend: {
      data: ["Collections", "Payments", "Net Flow"],
      top: 0,
      icon: "circle",
      itemGap: 15,
    },
    grid: { left: 60, right: 20, bottom: 30, top: 50 },
    xAxis: {
      type: "category",
      boundaryGap: true,
      data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisLabel: { color: "#334155" },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      axisLabel: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#000",
      },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    series: [
      {
        name: "Collections",
        type: "bar",
        data: sortedData.map((d) => d.Collections),
        itemStyle: { color: "#036b47" },
      },
      {
        name: "Payments",
        type: "bar",
        data: sortedData.map((d) => d.Payments),
        itemStyle: { color: "#ba2b2b" },
      },
      {
        name: "Net Flow",
        type: "line",
        smooth: true,
        data: sortedData.map((d) => d.NetFlow),
        itemStyle: { color: "#f59e0b" },
        lineStyle: { width: 3 },
      },
    ],
  };
};
export const getInflowDonutOption = (chartData) => {
  const total = chartData.reduce((sum, item) => sum + item.originalValue, 0);
  const colors = [
    "#854F0B",
    "#EF9F27",
    "#FAC775",
    "#FAEEDA",
    "#B8860B",
    "#C47A05",
  ];

  return {
    animation: false,
    tooltip: {
      trigger: "item",
      confine: true,
      formatter: ({ name, data: itemData, percent }) =>
        `${name}<br/><b>${formatTableCellValue(itemData.originalValue)} Cr</b> (${percent}%)`,
    },
    legend: { show: false },
    series: [
      {
        name: "Cash Inflow",
        type: "pie",
        radius: ["60%", "80%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderRadius: 6, borderColor: "#fff", borderWidth: 2 },
        data: chartData.map((d, i) => ({
          ...d,
          itemStyle: { color: colors[i % colors.length] },
        })),
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "42%",
        style: {
          text: formatTableCellValue(total),
          fontSize: 24,
          fontWeight: 700,
          fill: "#1e293b",
        },
      },
      {
        type: "text",
        left: "center",
        top: "56%",
        style: { text: "Total Inflow", fontSize: 12, fill: "#64748b" },
      },
    ],
  };
};

export const getOutflowBarChartOption = (chartData) => {
  const sortedData = [...(chartData || [])].sort(
    (a, b) => a.Actuals - b.Actuals,
  );
  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      formatter: "{b}: {c} Cr",
    },
    grid: { left: 20, right: 40, bottom: 30, top: 10, containLabel: true },
    xAxis: {
      type: "value",
      name: "Rs Cr",
      nameLocation: "middle",
      nameGap: 25,
      axisLabel: { color: "#334155" },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    yAxis: {
      type: "category",
      data: sortedData.map((d) => d.Description),
      axisLabel: {
        color: "#000",
        fontWeight: "bold",
        width: 140,
        overflow: "truncate",
      },
    },
    series: [
      {
        name: "Actuals (Cr)",
        type: "bar",
        data: sortedData.map((d) => d.Actuals),
        itemStyle: {
          color: new echarts.graphic.LinearGradient(1, 0, 0, 0, [
            { offset: 0, color: "#f87171" },
            { offset: 1, color: "#ba2b2b" },
          ]),
          borderRadius: [0, 4, 4, 0],
        },
      },
    ],
  };
};

export const getWeeklyBarChartOption = (chartData) => {
  if (!chartData || chartData.length === 0) return {};

  const collectionsRow = chartData.find(
    (d) => d.ParticularType === "CashInFlow",
  );
  const paymentsRow = chartData.find((d) => d.ParticularType === "CashOutFlow");

  if (!collectionsRow && !paymentsRow) return {};

  const weekKeys = Object.keys(collectionsRow || paymentsRow).filter(
    (k) => k.startsWith("WEEK") && k.endsWith("_Actuals"),
  );
  const labels = weekKeys.map((k) =>
    k.replace("_Actuals", "").replace("_", " "),
  );

  const collectionsData = weekKeys.map((k) => collectionsRow?.[k] ?? 0);
  const paymentsData = weekKeys.map((k) => paymentsRow?.[k] ?? 0);

  return {
    animation: false,
    tooltip: {
      trigger: "axis",
      axisPointer: { type: "shadow" },
      valueFormatter: (value) => `${formatTableCellValue(value)} Cr`,
    },
    legend: {
      data: ["Collections", "Payments"],
      top: 0,
      icon: "circle",
      itemGap: 15,
    },
    grid: { left: 60, right: 20, bottom: 30, top: 50 },
    xAxis: {
      type: "category",
      data: labels,
      axisLine: { lineStyle: { color: "#dbe4f5" } },
      axisLabel: { color: "#334155" },
    },
    yAxis: {
      type: "value",
      name: "Rs Cr",
      axisLabel: {
        fontSize: 12,
        fontWeight: "bold",
        color: "#000",
      },
      splitLine: { lineStyle: { color: "#eef2ff" } },
    },
    series: [
      {
        name: "Collections",
        type: "bar",
        data: collectionsData,
        itemStyle: { color: "#036b47" },
      },
      {
        name: "Payments",
        type: "bar",
        data: paymentsData,
        itemStyle: { color: "#ba2b2b" },
      },
    ],
  };
};

